/**
 * Die fuenf Zollspalten einer Kalkulationszeile - der Ausschnitt aus `ekTableRow`
 * (`index.html:10291-10295`), der an der Zolltarifgruppe haengt: Zolltarif, Zoll Gruppe, Zoll EUR,
 * Zoll JTL % und der berechnete Zollsatz.
 *
 * Die Komponente liefert reine `<td>`-Geschwister und steht darum ausschliesslich innerhalb der
 * Zeile in `PositionsTabelle.tsx`.
 *
 * ================= Die Tarifgruppe, das eine Ende des Zyklus (Auffaelligkeit 21) ================
 *
 * `r.customsGroup` ist genau die Referenz, die `calcEk` in `index.html:10192` setzt
 * (`r.customsGroup = group`), und `group.rows` enthaelt `r` wieder (`:10175`) - hier wird also das
 * eine Ende eines echten Zyklus gelesen. Gelesen wird nur `total` (fuer den Platzhalter der Spalte
 * „Zoll Gruppe"), und der Wert geht als Zeichenkette in das Markup. Weitergegeben wird die Gruppe
 * NICHT - kein Rueckruf, kein Store, kein `JSON.stringify`. Siehe `EkTab.tsx`, Kopfkommentar.
 *
 * ================= `group.conflict` wird im Bestand NIE gelesen (gemeldet) =================
 *
 * `calcEk` berechnet `group.conflict` (`index.html:10185`: zwei verschiedene Gruppenbetraege beim
 * selben Tarif, Abweichung > 0,01). Im gesamten Original liest das kein einziger Ausdruck - der
 * Zustand „widerspruechliche Gruppenbetraege" bleibt fuer den Benutzer unsichtbar. Woertlich
 * uebernommen: hier wird er ebenfalls nicht angezeigt. Im Task-Bericht gemeldet.
 */
import { inputNumber, moneyPlaceholder } from "./PositionsTabelle.tsx";
import type { EkRow } from "../../../lib/landedCost.ts";

/** `esc(s) = String(s ?? '')` (`index.html:4674`). */
const roh = (value: unknown): string => String(value ?? "");

export interface ZolltarifgruppenProps {
  zeile: EkRow;
  onPosition: (i: number, aenderung: Record<string, unknown>) => void;
}

export function Zolltarifgruppen({ zeile: r, onPosition }: ZolltarifgruppenProps) {
  const it = r.it;
  const patch = (schluessel: string, wert: unknown): void => onPosition(r.i, { [schluessel]: wert });

  // `index.html:10276`, woertlich - der Vergleich `> 0` an einem moeglicherweise fehlenden Feld.
  const customsGroupHint =
    (r.customsGroup?.total ?? 0) > 0 ? moneyPlaceholder(r.customsGroup?.total) : "0,00";

  /** `patchEkDutyActualLive` (`index.html:10013-10023`) - der Prozentsatz schreibt den Zollbetrag. */
  const dutyActualSetzen = (wert: string): void =>
    patch(
      "customsInvoice",
      wert === "" ? "" : (Math.max(0, Number(wert) || 0) * r.supplierShareEur) / 100,
    );

  return (
    <>
      <td className="ek-wide">
        <input
          type="text"
          inputMode="numeric"
          value={roh(it.customsTariff)}
          placeholder="Zolltarif"
          onChange={(e) => patch("customsTariff", e.currentTarget.value)}
        />
      </td>
      <td className="ek-num">
        <input
          type="number"
          min="0"
          step="0.01"
          value={roh(it.customsGroupAmount)}
          placeholder={customsGroupHint}
          onChange={(e) => patch("customsGroupAmount", e.currentTarget.value)}
        />
      </td>
      <td className="ek-num">
        <input
          type="number"
          min="0"
          step="0.01"
          value={roh(it.customsInvoice)}
          placeholder={moneyPlaceholder(r.customsShare)}
          onChange={(e) => patch("customsInvoice", e.currentTarget.value)}
        />
      </td>
      <td className="ek-num">
        <input
          type="number"
          min="0"
          step="0.01"
          value={roh(it.dutyPctJtl)}
          placeholder={r.dutyPctJtl ? inputNumber(r.dutyPctJtl, 2) : "0,00"}
          onChange={(e) => patch("dutyPctJtl", e.currentTarget.value)}
        />
      </td>
      {/* `class="${r.dutyMismatch ? 'strong' : ''}"` - der leere Wert ist Bestand (index.html:10295). */}
      <td className="ek-num ek-computed">
        <input
          className={r.dutyMismatch ? "strong" : ""}
          type="number"
          min="0"
          step="0.01"
          value={inputNumber(r.dutyPctActual)}
          onChange={(e) => dutyActualSetzen(e.currentTarget.value)}
        />
      </td>
    </>
  );
}
